import { useNavigation } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleProp, StyleSheet, Text, View, ViewStyle } from 'react-native';

import { testProps } from '../../../config';
import { RootStackParamList } from '../../navigator';
import GradientPlate, { GradientStops } from './GradientPlate';
import { TouchableOpacity } from './Touchable';

type Nav = NativeStackNavigationProp<RootStackParamList>;

type LoginButtonProps = {
	style?: StyleProp<ViewStyle>;
	testId?: string;
};

// Figma "Sign Up Button" (5911:55879), the top bar's LOG IN: 43 high and hugging its label with 14
// either side, a #66D93D - #248F1C (55%) - #0D540F plate in a #B8FF8C rim, rounded 10.
export const LOGIN_BUTTON = { height: 43, paddingX: 14, radius: 10 } as const;
const FILL: GradientStops = [
	['0', '#66D93D'],
	['0.55', '#248F1C'],
	['1', '#0D540F'],
];

const LoginButton = ({ style, testId = 'cta-login' }: LoginButtonProps) => {
	const { navigate } = useNavigation<Nav>();
	const { t } = useTranslation();
	const [width, setWidth] = useState(0);

	return (
		<TouchableOpacity
			accessibilityRole='button'
			activeOpacity={0.85}
			onPress={() => navigate('Login')}
			onLayout={event => setWidth(event.nativeEvent.layout.width)}
			style={[styles.button, style]}
			{...testProps(testId)}
		>
			{width > 0 ? (
				<GradientPlate
					width={width}
					height={LOGIN_BUTTON.height}
					radius={LOGIN_BUTTON.radius}
					stops={FILL}
					stroke='#B8FF8C'
					strokeWidth={1}
				/>
			) : null}
			<View style={styles.content}>
				<Text numberOfLines={1} style={styles.text}>
					{t('T_SITE_LOGIN_').toUpperCase()}
				</Text>
			</View>
		</TouchableOpacity>
	);
};

const styles = StyleSheet.create({
	button: {
		height: LOGIN_BUTTON.height,
		alignSelf: 'flex-start',
		borderRadius: LOGIN_BUTTON.radius,
		// Under the plate; Android only casts an elevation shadow from a filled view.
		backgroundColor: '#248F1C',
		// Figma: 0 0 9px rgba(64,255,51,0.45).
		shadowColor: '#40FF33',
		shadowOffset: { width: 0, height: 0 },
		shadowOpacity: 0.45,
		shadowRadius: 9,
		elevation: 4,
	},
	content: {
		height: '100%',
		paddingHorizontal: LOGIN_BUTTON.paddingX,
		justifyContent: 'center',
		alignItems: 'center',
	},
	// Sofia Sans Black 18 / 20, 0.36 tracked, with a dark green drop shadow.
	text: {
		color: '#F7FAF7',
		fontFamily: 'Sofia Sans',
		fontSize: 18,
		lineHeight: 20,
		fontWeight: '900',
		letterSpacing: 0.36,
		textShadowColor: 'rgba(0,64,20,0.6)',
		textShadowOffset: { width: 0, height: 1 },
		textShadowRadius: 2,
	},
});

export default LoginButton;
